/**
 * Booking permission utilities.
 *
 * Pure functions — no React/Redux dependencies. Fully testable.
 * All role-based booking logic lives here so components stay thin
 * and this layer can grow without touching UI code.
 */

export const BOOKING_CLIENT_ADVANCE_HOURS = 48;
export const BOOKING_NOTIFICATION_EMAIL = 'bookings@menorcahomecare.com';

/**
 * Returns true if the booking can be edited.
 * Clients cannot edit bookings whose departure date has already passed.
 *
 * @param {string} role  - Auth role ('client', 'admin', …)
 * @param {number} endDate - Booking end_date as timestamp (ms)
 */
export function canEditBooking(role, endDate) {
  if (role !== 'client') return true;
  if (!endDate) return true;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return endDate >= todayStart.getTime();
}

/**
 * Returns an array of 'YYYY-MM-DD' date strings that must be disabled
 * in the start-date picker for client users (the 48-hour advance window).
 *
 * @param {string} role
 * @returns {string[]}
 */
export function getDisabledStartDatesForClient(role) {
  if (role !== 'client') return [];

  const disabled = [];
  const threshold = Date.now() + BOOKING_CLIENT_ADVANCE_HOURS * 60 * 60 * 1000;

  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0); // start of today

  while (cursor.getTime() < threshold) {
    disabled.push(_formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return disabled;
}

/**
 * Validates that a start date respects the 48-hour advance rule for clients.
 * Returns an error string or empty string if valid.
 *
 * @param {string} role
 * @param {number} startDate - timestamp (ms)
 * @returns {string}
 */
export function validateClientAdvanceDate(role, startDate) {
  if (role !== 'client') return '';
  const minStartMs = Date.now() + BOOKING_CLIENT_ADVANCE_HOURS * 60 * 60 * 1000;
  if (startDate < minStartMs) {
    return `Bookings must be created at least ${BOOKING_CLIENT_ADVANCE_HOURS} hours in advance`;
  }
  return '';
}

/**
 * Returns the label for the arrival requirements textarea.
 *
 * @param {string} role
 * @returns {string}
 */
export function getArrivalRequirementsLabel(role) {
  return role === 'client' ? 'Arrival requirements' : 'Arrival special requirements';
}

/**
 * Returns the label for the departure requirements textarea.
 *
 * @param {string} role
 * @returns {string}
 */
export function getDepartureRequirementsLabel(role) {
  return role === 'client' ? 'Departure requirements' : 'Departure special requirements';
}

/**
 * Returns an explicit title string for the cleaning special services section,
 * or null to fall through to the default IntlMessages key.
 *
 * @param {string} role
 * @returns {string|null}
 */
export function getCleaningSpecialServicesTitle(role) {
  return role === 'client' ? 'Services During stay' : null;
}

// ── private helpers ───────────────────────────────────────────────────────────

function _formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
